import { useState, useEffect } from "react";
import { useSession } from "next-auth/client";
import {
  Text,
  Textarea,
  Button,
  Box,
  Popover,
  PopoverTrigger,
  IconButton,
  PopoverContent,
  PopoverHeader,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  HStack,
  VStack,
  useColorModeValue,
  Tooltip
} from "@chakra-ui/react";
import { FiSettings } from "react-icons/fi";
import { BsInfoCircle } from "react-icons/bs";
import { GRADIENT_COLOR } from "./utils";
import { getAccount, updateUser } from "utils/sessionHelper";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { SettingsIcon } from "./icons/settings-icon";
import { layerStyle, radius, textStyle } from "theme/names";
import { secondary } from "theme/components/button/secondary";
import { colors } from "theme/colors/colors";
import { layerStyles } from "theme/layerStyles/layerStyles";
import { SaveIcon } from "components/icons/SaveIcon";
import { SaveIconComponent } from "components/icons/SaveIconComponent";

export function SettingsButton() {
  const [who, setWho] = useState("");
  const [topics, setTopics] = useState("");
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [session] = useSession();
  const isAuthenticated = useFirebaseUser();
  const textColor = useColorModeValue("blue.400", "blue.300");

  useEffect(() => {
    if (isAuthenticated && !hasFetched) {
      setHasFetched(true);
      setWho(getAccount(session)?.who || "");
      setTopics(getAccount(session)?.topics || "");
    }
  }, [session, isAuthenticated]);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      await updateUser(session, { who, topics });
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Popover isLazy>
      {/* <Tooltip label="Settings" placement="top"> */}
      <PopoverTrigger>
        <IconButton
          aria-label="Open settings"
          icon={<SettingsIcon />}
          variant="secondaryAI"
          size="sm"
          color="#D792FF !important"
        />
      </PopoverTrigger>
      {/* </Tooltip> */}
      <PopoverContent
        // width="400px"
        // layerStyle={layerStyle["bg.border.rounded"]}
        {...{
          ...layerStyles.bgBorderRounded,
          borderRadius: "10px",
          borderBottom: "none",
          shadow: "2xl",
          // _after: {
          //     content: '""',
          //     position: "absolute",
          //     bottom: 0,
          //     left: 0,
          //     width: "100%",
          //     height: "4px",
          //     background: GRADIENT_COLOR,
          //     borderTop: radius.lg,
          // }
          // chin of the modal part 1
          _before: {
            content: "''",
            position: "absolute",
            bottom: "-4px",
            left: "-1px",
            width: "calc(100% + 2px)",
            height: "16px",
            borderBottomRadius: "10px",
            background: GRADIENT_COLOR,
          },
          // chin of the modal part 2
          _after: {
            content: "''",
            position: "absolute",
            bottom: "0px",
            left: "-1px",
            width: "calc(100% + 0px)",
            height: "16px",
            borderBottomRadius: "10px",
            background: "white",
            ...layerStyles.bgBorderRounded,
            borderTopRadius: "0px",
            borderTop: "none",
          },
          _dark: {
            bg: "neutral.darkMode.neutral",
            border: "1px solid",
            borderColor: "border.darkMode.light",
            _after: {
              background: "#1E1E1E",
              borderTopRadius: "0px",
              border: "1px solid",
              borderColor: "border.darkMode.light",
              borderTop: "none",
            },
          },
        }}
      // zIndex="999999999"
      >
        <PopoverHeader display="flex" alignItems="center" px="4" pt="4" pb="4" gap={2}
          {...{
            borderBottom: "1px solid",
            borderColor: colors.border.lightMode.light,
            _dark: {
              borderColor: colors.border.darkMode.light,
            }
          }}>
          {/* <FiSettings size={16} />{" "} */}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 8C5 6.34375 6.3125 5 8 5C9.65625 5 11 6.34375 11 8C11 9.65625 9.65625 11 8 11C6.3125 11 5 9.65625 5 8ZM8 6.5C7.15625 6.5 6.5 7.1875 6.5 8C6.5 8.84375 7.15625 9.5 8 9.5C8.8125 9.5 9.5 8.84375 9.5 8C9.5 7.1875 8.8125 6.5 8 6.5ZM9.15625 0C9.84375 0 10.4375 0.5 10.5938 1.15625L10.8438 2.1875C11.125 2.3125 11.375 2.46875 11.5938 2.625L12.625 2.3125C13.2812 2.125 14 2.40625 14.3438 3L15.5 5.03125C15.8438 5.625 15.7188 6.375 15.2188 6.84375L14.4688 7.5625C14.4688 7.71875 14.5 7.875 14.5 8C14.5 8.15625 14.4688 8.3125 14.4688 8.4375L15.2188 9.1875C15.7188 9.65625 15.8438 10.4062 15.5 11L14.3438 13.0312C14 13.625 13.2812 13.9062 12.625 13.7188L11.5938 13.4062C11.375 13.5625 11.125 13.7188 10.8438 13.8438L10.5938 14.875C10.4375 15.5312 9.84375 16 9.15625 16H6.8125C6.125 16 5.53125 15.5312 5.375 14.875L5.125 13.8438C4.84375 13.7188 4.59375 13.5625 4.375 13.4062L3.34375 13.7188C2.6875 13.9062 1.96875 13.625 1.625 13.0312L0.46875 11C0.125 10.4062 0.25 9.65625 0.75 9.1875L1.5 8.4375C1.5 8.3125 1.5 8.15625 1.5 8C1.5 7.875 1.5 7.71875 1.5 7.5625L0.75 6.84375C0.25 6.375 0.125 5.625 0.46875 5.03125L1.625 3C1.96875 2.40625 2.6875 2.125 3.34375 2.3125L4.375 2.625C4.59375 2.46875 4.84375 2.3125 5.125 2.1875L5.375 1.15625C5.53125 0.5 6.125 0 6.8125 0H9.15625ZM6.40625 3.25L6.0625 3.40625C5.65625 3.5625 5.28125 3.78125 4.96875 4.03125L4.65625 4.28125L2.9375 3.75L1.78125 5.78125L3.0625 7L3.03125 7.375C3 7.59375 3 7.8125 3 8C3 8.21875 3 8.4375 3.03125 8.65625L3.0625 9.03125L1.78125 10.25L2.9375 12.2812L4.65625 11.75L4.96875 12C5.28125 12.25 5.65625 12.4688 6.0625 12.625L6.40625 12.7812L6.8125 14.5H9.15625L9.5625 12.7812L9.90625 12.625C10.3125 12.4688 10.6875 12.25 11 12L11.3125 11.75L13.0312 12.2812L14.1875 10.25L12.9062 9.03125L12.9375 8.65625C12.9688 8.4375 13 8.21875 13 8C13 7.8125 12.9688 7.59375 12.9375 7.375L12.9062 7L14.1875 5.78125L13.0312 3.75L11.3125 4.28125L11 4.03125C10.6875 3.78125 10.3125 3.5625 9.90625 3.40625L9.5625 3.25L9.15625 1.5H6.8125L6.40625 3.25Z" fill="url(#paint0_linear_77_30524)" />
            <defs>
              <linearGradient id="paint0_linear_77_30524" x1="0.4" y1="-0.2" x2="16.6" y2="16.2" gradientUnits="userSpaceOnUse">
                <stop stopColor="#EC6181" />
                <stop offset="1" stopColor="#316BFF" />
              </linearGradient>
            </defs>
          </svg>

          <Text
            // ml="2"
            // bgGradient={GRADIENT_COLOR}
            // bgClip="text"
            // fontWeight="bold"
            textStyle={textStyle["h4.bold.standard"]}
          >
            AI settings
          </Text>
        </PopoverHeader>
        {/* <PopoverArrow /> */}
        <PopoverCloseButton mt="3" mr="2" {...{ ...secondary, fontSize: "10px", borderRadius: radius.md }} />
        <PopoverBody pt="3" pb="4" px="4">
          <HStack
            // color={textColor}
            textStyle={textStyle["body.medium.light"]}
            alignItems="flex-start">
            <Box mt="0.5">
              <BsInfoCircle size={16} />
            </Box>
            <Text fontSize="sm">
              The best way to improve the results of our AI is for it to
              understand how you want to position yourself on X.
            </Text>
          </HStack>
          <VStack mt="6" spacing="4" fontSize="sm">
            <Text
              textStyle={textStyle["body.medium.standard"]}
              // color={textColor}
              w="100%" textAlign="left">
              You are...
            </Text>
            <Textarea
              textStyle={textStyle["body.medium.light"]}
              fontSize="sm"
              placeholder="Personal description..."
              value={who}
              onChange={(e) => setWho(e.target.value)}
            />
          </VStack>
          <VStack mt="8" spacing="3" fontSize="sm">
            <Text
              textStyle={textStyle["body.medium.standard"]}
              // color={textColor}
              w="100%" textAlign="left">
              What you talk about...
            </Text>
            <Textarea
              textStyle={textStyle["body.medium.light"]}
              fontSize="sm"
              placeholder="Personal description..."
              value={topics}
              onChange={(e) => setTopics(e.target.value)}
            />
          </VStack>
          <Box mt="4" mb="1" w="100%" textAlign="right">
            <Button
              variant={"primaryAI"}
              // size="sm"
              isLoading={isLoading}
              onClick={handleSave}
              zIndex="99"
              leftIcon={SaveIcon}
            >
              Save
            </Button>
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
